import React from "react";
import { Sparkles, ArrowUpRight } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || obj.es || "";
};

const COPY = {
  overline: {
    en: "AI Concierge",
    fr: "Conciergerie IA",
    es: "Conserje IA",
  },
  title: {
    en: "Any question? Ask the concierge.",
    fr: "Une question ? Demandez au concierge.",
    es: "¿Alguna duda? Consulta al conserje.",
  },
  body: {
    en: "Rooms, services, spa, gastronomy, how to get here, or a specific booking — our AI concierge is trained on {name} and answers in seconds, day or night.",
    fr: "Chambres, services, spa, gastronomie, comment venir ou une réservation précise — notre conciergerie IA est formée sur {name} et répond en quelques secondes, jour et nuit.",
    es: "Habitaciones, servicios, spa, gastronomía, cómo llegar o una reserva concreta — nuestro conserje IA está formado sobre {name} y responde en segundos, día y noche.",
  },
  cta: {
    en: "Chat with the AI Concierge",
    fr: "Discuter avec la Conciergerie IA",
    es: "Hablar con el Conserje IA",
  },
  hint: {
    en: "Available 24/7 · English, French, Spanish",
    fr: "Disponible 24h/24 · Anglais, français, espagnol",
    es: "Disponible 24/7 · Inglés, francés, español",
  },
};

// Compact CTA rendered inside the About column, right below the location
// map, inviting guests to open the on-page AI concierge widget. Fires the
// same global `xaluca:open-chat` event used by every other trigger point
// on the site so behaviour stays consistent.
const HotelConciergeCTA = ({ hotelSlug, hotelName }) => {
  const { lang } = useLanguage();

  const openChat = () => {
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("xaluca:open-chat", { detail: { hotelSlug, hotelName } })
      );
    }
  };

  const body = pick(COPY.body, lang).replace("{name}", hotelName || "");

  return (
    <aside
      className="mt-6 relative overflow-hidden border border-[#2C2621]/10 bg-[#1A1513] text-[#FDFBF7] p-5 md:p-6"
      data-testid={`concierge-cta-${hotelSlug || "default"}`}
      data-cms-scope={hotelSlug ? `hotel.${hotelSlug}.concierge-cta` : "hotel.concierge-cta"}
    >
      {/* Subtle Amazigh-tinted gradient sheen — non-interactive */}
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#C16542]/25 via-transparent to-transparent"
        aria-hidden="true"
      />

      <div className="relative flex items-start gap-3">
        <span
          className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#C16542]/20 border border-[#C16542]/40 shrink-0"
          aria-hidden="true"
        >
          <Sparkles className="w-4 h-4 text-[#C16542]" strokeWidth={1.75} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] tracking-[0.28em] uppercase text-[#C16542]" data-testid="concierge-cta-overline">
            {pick(COPY.overline, lang)}
          </p>
          <h3
            className="font-serif-x text-lg md:text-xl leading-tight mt-1"
            data-testid="concierge-cta-title"
          >
            {pick(COPY.title, lang)}
          </h3>
          <p
            className="mt-2.5 text-[13px] md:text-sm leading-relaxed text-[#FDFBF7]/85"
            data-testid="concierge-cta-body"
          >
            {body}
          </p>
        </div>
      </div>

      <div className="relative mt-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <button
          type="button"
          onClick={openChat}
          className="inline-flex items-center justify-center gap-2 bg-[#C16542] hover:bg-[#A35133] active:bg-[#8B4128] text-[#FDFBF7] px-5 py-3 text-[11px] tracking-[0.24em] uppercase transition-colors focus:outline-none focus:ring-2 focus:ring-[#C16542]/50 focus:ring-offset-2 focus:ring-offset-[#1A1513]"
          data-testid="concierge-cta-open"
          aria-label={pick(COPY.cta, lang)}
        >
          {pick(COPY.cta, lang)}
          <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2} />
        </button>
        <p
          className="text-[10px] tracking-[0.14em] uppercase text-[#FDFBF7]/60"
          data-testid="concierge-cta-hint"
        >
          {pick(COPY.hint, lang)}
        </p>
      </div>
    </aside>
  );
};

export default HotelConciergeCTA;
